"use client";

import { c as _c } from "react/compiler-runtime";
import { useEffect, useRef } from "react";
import { useMap } from "@vis.gl/react-google-maps";

/**
 * Extracts the structured address parts the v2 lead-gen API needs
 * (city/state/stateCode/pincode/locality/country) out of a Geocoder result's
 * address_components, alongside the position we already have.
 */
function extractAddressComponents(result, position) {
  const components = result?.address_components || [];
  const find = type => components.find(c => c.types.includes(type));
  return {
    locality: find("locality")?.long_name || find("sublocality")?.long_name || null,
    city: find("locality")?.long_name || find("administrative_area_level_2")?.long_name || null,
    state: find("administrative_area_level_1")?.long_name || null,
    stateCode: find("administrative_area_level_1")?.short_name || null,
    pincode: find("postal_code")?.long_name || null,
    country: find("country")?.long_name || null,
    lat: position?.lat ?? null,
    lng: position?.lng ?? null
  };
}

/**
 * Component to handle reverse geocoding (converting coordinates to address)
 * Automatically geocodes the position and calls onAddressChange with the
 * formatted address string plus a structured components object.
 *
 * @param {Object} props
 * @param {Object} props.position - { lat: number, lng: number }
 * @param {Function} props.onAddressChange - Callback(address: string, components: object)
 */
export default function Geocoder(t0) {
  const $ = _c(5);
  const {
    position,
    onAddressChange
  } = t0;
  const map = useMap();
  const geocoderRef = useRef(null);
  const isGeocodingRef = useRef(false);
  let t1;
  let t2;
  if ($[0] !== map || $[1] !== onAddressChange || $[2] !== position) {
    t1 = () => {
      if (!map || !position || !window.google || isGeocodingRef.current) {
        return;
      }
      if (!geocoderRef.current) {
        geocoderRef.current = new window.google.maps.Geocoder();
      }
      isGeocodingRef.current = true;
      geocoderRef.current.geocode({
        location: {
          lat: position.lat,
          lng: position.lng
        }
      }, (results, status) => {
        isGeocodingRef.current = false;
        if (status === "OK" && results && results.length > 0) {
          let bestAddress = results[0].formatted_address;
          const plusCodePattern = /^[A-Z0-9]{2,}\+[A-Z0-9]{2,}/;
          for (const result of results) {
            const address = result.formatted_address;
            if (plusCodePattern.test(address.split(",")[0].trim())) {
              continue;
            }
            const hasStreetName = result.types.includes("street_address") || result.types.includes("route") || result.types.includes("premise");
            if (hasStreetName || address.length > 20) {
              bestAddress = address;
              break;
            }
          }
          if (plusCodePattern.test(bestAddress.split(",")[0].trim())) {
            const components = results[0].address_components || [];
            const addressParts = [];
            const streetNumber = components.find(_temp)?.long_name;
            const route = components.find(_temp2)?.long_name;
            const neighborhood = components.find(_temp3)?.long_name;
            const locality = components.find(_temp4)?.long_name;
            const adminArea = components.find(_temp5)?.short_name;
            const postalCode = components.find(_temp6)?.long_name;
            const country = components.find(_temp7)?.long_name;
            if (streetNumber && route) {
              addressParts.push(`${streetNumber} ${route}`);
            } else {
              if (route) {
                addressParts.push(route);
              } else {
                if (neighborhood) {
                  addressParts.push(neighborhood);
                }
              }
            }
            if (locality) {
              addressParts.push(locality);
            }
            if (adminArea) {
              addressParts.push(adminArea);
            }
            if (postalCode) {
              addressParts.push(postalCode);
            }
            if (country) {
              addressParts.push(country);
            }
            if (addressParts.length > 0) {
              bestAddress = addressParts.join(", ");
            }
          }
          onAddressChange(bestAddress, extractAddressComponents(results[0], position));
        } else {
          onAddressChange("Address not available", null);
        }
      });
    };
    t2 = [map, position, onAddressChange];
    $[0] = map;
    $[1] = onAddressChange;
    $[2] = position;
    $[3] = t1;
    $[4] = t2;
  } else {
    t1 = $[3];
    t2 = $[4];
  }
  useEffect(t1, t2);
  return null;
}
function _temp7(c_5) {
  return c_5.types.includes("country");
}
function _temp6(c_4) {
  return c_4.types.includes("postal_code");
}
function _temp5(c_3) {
  return c_3.types.includes("administrative_area_level_1");
}
function _temp4(c_2) {
  return c_2.types.includes("locality");
}
function _temp3(c_1) {
  return c_1.types.includes("neighborhood");
}
function _temp2(c_0) {
  return c_0.types.includes("route");
}
function _temp(c) {
  return c.types.includes("street_number");
}